import React, { useState } from 'react';
import { FlatList, Modal } from "react-native";
import type { AccountActionProps, AccountProps, ItemOrderProps, MyPlayerProps, PlayerBalanceProps, PlayerDepositLimitProps, PlayerWithdrawLimitProps, WalletSettingsProps } from '../../types';
import { Icons } from '../../Components';
import { WalletHelpers } from '../api';
import DepositCard from './DepositCard';
import WithdrawCard from './WithdrawCard';
import AccountLimitManager from './AccountLimitManager';
import AccountSnoozeManager from './AccountSnoozeManager';
import RemoveAccountCard from './RemoveAccount';
import { useColors } from '../../constants/useColors';
import { Button, Text, View } from '../../Components/Themed';

type ManageAccountCardProps = {
    player:MyPlayerProps,
    wallet_settings:WalletSettingsProps,
    player_balance:PlayerBalanceProps,
    account:AccountProps,
    insets?:{ top:number, bottom:number, left:number, right:number },
    view_mode?: 'desktop'|'mobile',
    deposit_limit:PlayerDepositLimitProps,
    withdraw_limit:PlayerWithdrawLimitProps,
    onClose:() => void,
    onAccountUpdate:(acct:AccountProps) => void,
    onTransact:(draft_order:ItemOrderProps, account_id?:string) => void
}
const manage_sections = ['header','actions']

const ManageAccountCard = ({ player, view_mode, insets, wallet_settings, player_balance, account, deposit_limit, withdraw_limit, onAccountUpdate, onTransact, onClose }:ManageAccountCardProps) => {
    const Colors = useColors();
    const [ active_action, setActiveAction ] = useState('select');
    const account_actions = WalletHelpers.getAccountOptions(account)

    const renderIcon = (action:AccountActionProps) => {
        switch(action.action_type){
            case 'deposit':
                return <Icons.DepositIcon color={Colors.text.white} size={20} />
            case 'instant_withdraw':
                return <Icons.WithdrawIcon color={Colors.text.gold} size={20} />
            case 'withdraw':
                return <Icons.WithdrawIcon color={Colors.text.white} size={20} />
            case 'limits':
                return <Icons.BankIcon color={Colors.text.white} size={28}/>
            case 'snoozes':
                return <Icons.SnoozeIcon color={Colors.text.white} size={18} />
            case 'remove':
                return <Icons.CloseIcon color={Colors.text.white} size={14} />
            default: return <></>
        }
    }

    const renderAccountActions = (data: { item:AccountActionProps, index:number }) => {
        if(!account){ return <></> }
        if(account.ibt_placeholder && !['deposit'].includes(data.item.action_type)){ return <></> }
        if(data.item.action_type == 'withdraw' && account.account_type == 'paypal'){ return <></> }
        if(data.item.action_type == 'remove' && account.ibt_placeholder){ return <></> }
        if(data.item.action_type == 'withdraw' && account.account_type == 'card'){ return <></> }
        if(data.item.action_type == 'remove' && account.account_type === 'paypal'){ return <></> }
        if(data.item.action_type == 'instant_withdraw' && !account.ach_detail){ return <></> }
        if(data.item.action_type == 'instant_withdraw' && !account.ach_detail?.instant_allowed){ return <></> }
        //if(data.item.action_type == 'deposit' && account.account_type == 'card' && account?.card_detail?.card_type != 'debit'){ return <></> }
        return (
            <Button float style={{ flex:1, minHeight:80, margin:8 }} onPress={() => setActiveAction(data.item.action_type)}>
                <View transparent style={{flex:1, flexDirection:'row', alignItems:'center'}}>
                    <View float style={{height:36, width:36, backgroundColor:Colors.text.action, justifyContent:'center', alignItems:'center', borderRadius:3.6 }}>
                        {renderIcon(data.item)}
                    </View>                            
                    <View transparent style={{marginLeft:16, marginRight:10, flex:1, padding:2}}>
                        <Text size={14} theme='h1'>{data.item.action_title}</Text>
                        <Text style={{ marginTop:2 }} theme='description'>{data.item.action_description}</Text>
                    </View>
                    <Icons.ChevronIcon direction='right' color={Colors.text.h1} size={12} />                       
                </View>
            </Button>
        )
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:15 }}>
                        <View transparent style={{flex:1}}>
                            <Text size={20} theme='h1'>{account.account_label}</Text>
                            {active_action == 'select' ?
                            <Text style={{ marginTop:5}} theme='description'>Below are the available actions that can be performed on this account!</Text>
                            :<></>}
                        </View>
                        {account.ach_detail && account.ach_detail.ach_type == 'ibf' ?
                        <View transparent style={{ marginLeft:10 }}>
                            <Icons.BankInstantIcon color={Colors.text.gold} size={20} />
                        </View>
                        :<></>}
                    </View>
                )
            case 'actions':
                return (
                    <FlatList 
                        data={account_actions} 
                        key={`account_actions`}
                        renderItem={renderAccountActions} 
                        keyExtractor={(item) => item.action_id}
                    />
                )
            
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }}>
                <FlatList
                    key={'account_stuff'}
                    keyExtractor={(item) => item}
                    data={manage_sections}
                    renderItem={renderSections}
                />
            </View>
            {active_action == 'select' ?
            <View type='footer' style={{ flexDirection:'row', padding:20 }}>
                <Button
                    title='BACK'
                    type='close'
                    style={{ flex:1 }}
                    onPress={() => onClose()}
                />
            </View>
            :<></>}
            <Modal
                style={{ flex:1 }}
                animationType={view_mode == 'desktop' ? 'fade': 'slide'}
                transparent
                visible={active_action != 'select' ? true : false }
            >
                <View type='blur' style={view_mode == 'desktop' ?{ flex:1, justifyContent:'center' , alignItems:'center' }: { flex:1 }}>
                <View style={view_mode == 'desktop' ? { height:700, width:600 } : { flex:1, paddingTop: insets?.top, paddingBottom: insets?.bottom }}>
                    {active_action == 'deposit' ?
                    <DepositCard
                        player={player}
                        wallet_settings={wallet_settings}
                        account={account}
                        deposit_limit={deposit_limit}
                        onTransact={onTransact}
                        onCancel={() => setActiveAction('select')}
                    />
                    :active_action == 'withdraw' ?
                    <WithdrawCard
                        player={player}
                        deposit_limit={deposit_limit}
                        wallet_settings={wallet_settings}
                        player_balance={player_balance}
                        account={account}
                        onAccountUpdate={onAccountUpdate}
                        withdraw_limit={withdraw_limit}
                        onTransact={onTransact}
                        onCancel={() => setActiveAction('select')}
                    />
                    :active_action == 'instant_withdraw'?
                    <WithdrawCard
                        player={player}
                        instant
                        deposit_limit={deposit_limit}
                        wallet_settings={wallet_settings}
                        player_balance={player_balance}
                        account={account}
                        onAccountUpdate={onAccountUpdate}
                        withdraw_limit={withdraw_limit}
                        onTransact={onTransact}
                        onCancel={() => setActiveAction('select')}
                    />
                    :active_action == 'limits'?
                    <AccountLimitManager
                        account={account}
                        onCancel={() => setActiveAction('select')}
                        onSetLimit={(al) => console.log(al)}
                    />
                    :active_action == 'snoozes'?
                    <AccountSnoozeManager
                        account={account}
                        onCancel={() => setActiveAction('select')}
                        onSnooze={(snz) => console.log(snz)}
                    />
                    :active_action == 'remove'?
                    <RemoveAccountCard
                        account={account}
                        onCancel={() => setActiveAction('select')}
                        onRemove={(acct) => {
                            onAccountUpdate(acct)
                            onClose()
                        }}
                    />
                    :<></>}
                </View>
                </View>
                
            </Modal>
        </View>
    )
}

export default ManageAccountCard